import React, { useState, useEffect, useRef, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import {
  generateTestWords,
  selectCorrectWord,
  generateOptionWords,
  getClues,
  getFTUEData,
} from "./helpers";
import { Container } from "../shared";
import useConversation from "../shared/useConversation";
import { listen } from "Services/Listen";
import "./GSF.scss";

import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";
import GSFContent from "./GSFContent";
import GSFResultService from "./result-service";
import { getUserData } from "Services/API";

let ttsStrings;

export default function GSFValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.GSF;

  const isValid =
    props.wordBankMetadata &&
    props.wordBankMetadata.words &&
    props.wordBankMetadata.words.length > 0 &&
    props.wordBankMetadata.categories &&
    props.wordBankMetadata.categories.length > 0;

  const isMountedRef = useIsMounted();

  const [hardMode, setHardMode] = useState(GSFResultService.isHardMode);

  useEffect(() => {
    setHardMode(GSFResultService.isHardMode);
  }, [GSFResultService.isHardMode]);

  useEffect(() => {
    if (!isValid) close();
  }, []);

  if (!isValid) return null;

  const userLocale = useMemo(() => getUserData().locale, []);
  const tutorInSpanish = useMemo(() => getUserData().tutorInSpanish, []);
  const ftueData = getFTUEData(userLocale, tutorInSpanish);

  return (
    <GSF
      {...props}
      wordBank={props.wordBankMetadata.words}
      categoryMap={props.wordBankMetadata.categories.reduce((acc, category) => {
        acc[category.id] = category.description;
        return acc;
      }, {})}
      ftueData={ftueData}
      close={close}
      dispatchEvent={dispatchEvent}
      isMountedRef={isMountedRef}
      hardMode={hardMode}
    />
  );

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function GSF(props) {
  const { wordBank, categoryMap, speech, ftue, ftueData, isiPad, hardMode, logSessionInfo } = props;

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [testWords, setTestWords] = useState([]);
  const [correctWord, setCorrectWord] = useState("");
  const [optionWords, setOptionWords] = useState([]);
  const [hiddenOptionWords, setHiddenOptionWords] = useState([]);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isIncorrect, setIsIncorrect] = useState(false);
  const [selectionWord, setSelectionWord] = useState();
  const [highlightedIndices, setHighlightedIndices] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [puzzleEnded, setPuzzleEnded] = useState(false);

  const { speak } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const timer = useRef(undefined);
  const testWordHistory = useRef([]);
  const incorrectCount = useRef(0);
  const handlingIncorrect = useRef(false);
  const correctWordRef = useRef(correctWord);
  const testWordsRef = useRef(correctWord);
  const puzzleEndedRef = useRef(false);
  const hardModeRef = useRef(hardMode);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    testWordsRef.current = testWords;
  }, [testWords]);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    correctWordRef.current = correctWord;
  }, [correctWord]);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    puzzleEndedRef.current = puzzleEnded;
  }, [puzzleEnded]);

  useEffect(() => {
    if (!props.isMountedRef.current) {
      return;
    }

    hardModeRef.current = hardMode;
  }, [hardMode]);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const onStageChange = async () => {
    if (!props.isMountedRef.current) {
      return;
    }

    switch (stage) {
      case 0:
        await HandleStageZero();
        break;
      case 1:
        await HandleStageOne();
        break;
      case 2:
        await handleStageTwo();
        break;
    }
  }

  const generateTest = () => {
    const newTestWords = generateTestWords(wordBank, testWordHistory.current, hardModeRef.current);
    const newCorrectWord = selectCorrectWord(newTestWords, hardModeRef.current);
    const newOptionWords = generateOptionWords(
      newTestWords,
      newCorrectWord,
      wordBank
    );
    setTestWords(newTestWords);
    setCorrectWord(newCorrectWord);
    setOptionWords(newOptionWords);
    testWordHistory.current = [...testWordHistory.current, ...newTestWords];
    setPuzzleEnded(false);
  }

  const resetTest = () => {
    generateTest();
    setIsCorrect(false);
    setIsIncorrect(false);
    setShowAnswer(false);
    setSelectionWord(undefined);
    setHiddenOptionWords([]);
    setHighlightedIndices([]);
    incorrectCount.current = 0;
  }

  // FTUE only stage - FTUE is defined if invervention of this type is served for the first time in the current session.
  const HandleStageZero = async () => {
    try {
      setTestWords(ftueData.testWords);
      setCorrectWord(ftueData.correctWord);
      setOptionWords(ftueData.optionWords);
      await cancellablePromise(speak(ttsStrings.ftue_intro));
      await cancellablePromise(sleep(300));
      setHighlightedIndices([0, 2]);
      await cancellablePromise(speak(ttsStrings.ftue_column_1));
      await cancellablePromise(sleep(1000));
      setHighlightedIndices([1, 3]);
      await cancellablePromise(speak(ttsStrings.ftue_column_2));
      await cancellablePromise(sleep(1000));
      setHighlightedIndices([0, 1]);
      await cancellablePromise(speak(ttsStrings.ftue_row_1));
      await cancellablePromise(sleep(1000));
      setHighlightedIndices([2, 3]);
      await cancellablePromise(speak(ttsStrings.ftue_row_2));
      await cancellablePromise(sleep(1000));
      setHighlightedIndices([]);
      await cancellablePromise(speak(ttsStrings.ftue_find_word_1));
      await cancellablePromise(sleep(750));
      const speakPromise = speak(isiPad ? ttsStrings.ftue_find_word_2_iPad : ttsStrings.ftue_find_word_2);
      setTimeout(() => setShowAnswer(true), 1000);
      const animationPromise = sleep(2500);
      await cancellablePromise(Promise.all([speakPromise, animationPromise]));
      setIsCorrect(true);
      await cancellablePromise(sleep(500));
      await cancellablePromise(speak(ttsStrings.ftue_your_turn));
      goToNextStage();
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  }

  // Non-FTUE will start from this stage
  const HandleStageOne = async () => {
    try {
      resetTest();
      if (ftue) {
        await cancellablePromise(speak(ttsStrings.choose_word));
      } else {
        await cancellablePromise(speak(ttsStrings.non_ftue_intro));
      }
      startGuessTimer();
      setInteractive(true);
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  }

  const handleStageTwo = async () => {
    try {
      await cancellablePromise(speak(ttsStrings.one_more));
      resetTest();
      startGuessTimer();
      setInteractive(true);
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  }

  const goToNextStage = async () => {
    if (!props.isMountedRef.current) {
      return;
    }

    if (stage === 2) {
      try {
        // End of intervention
        await cancellablePromise(speak(ttsStrings.great_work));
        props.close();
      } catch (error) {
        if (error.isCancelled) return;
        throw error;
      }
      return;
    }

    setStage(stage + 1);
  }

  const startGuessTimer = () => {
    timer.current = setTimeout(() => {
      handleIncorrect(
        true // fromTimeout
      );
    }, 20000);
  }

  const handleGuess = async (guessWord) => {
    if (!props.isMountedRef.current || puzzleEndedRef.current) {
      return;
    }

    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      if (guessWord === correctWordRef.current || !handlingIncorrect.current) {
        // Only hide the option if it is either 1) the correct word or 2) we are not already handling an incorrect scenario
        setHiddenOptionWords([...hiddenOptionWords, guessWord]);
      }

      if (guessWord === correctWordRef.current) {
        logSessionInfo('GSF_puzzle_success', { attempt: incorrectCount.current + 1, hardMode: hardModeRef.current });
        GSFResultService.addPuzzleResult(1);
        setIsCorrect(true);
        setIsIncorrect(false);
        setInteractive(false);
        setPuzzleEnded(true);

        const { category, word1, word2, letter } = getClues(
          testWordsRef.current,
          correctWordRef.current,
          categoryMap
        );

        await cancellablePromise(
          speak(
            ttsStrings.correct({
              correctWord: correctWordRef.current,
              category,
              word1,
              letter,
              word2,
            })
          )
        );

        goToNextStage();
      } else {
        handleIncorrect(
          false, // fromTimeout
          guessWord
        );
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw error;
    }
  }

  const handleIncorrect = async (fromTimeout, guessWord) => {
    if (!props.isMountedRef.current || puzzleEndedRef.current || incorrectCount.current >= 3 || handlingIncorrect.current) {
      return;
    }

    handlingIncorrect.current = true;

    if (timer.current) {
      clearTimeout(timer.current);
    }

    setInteractive(false);

    const currIncorrect = incorrectCount.current;
    incorrectCount.current = currIncorrect + 1;

    if (!fromTimeout && guessWord) {
      setIsIncorrect(true);
      setIsCorrect(false);
      setSelectionWord(guessWord);
    }

    const { category, word1, word2, letter } = getClues(
      testWordsRef.current,
      correctWordRef.current,
      categoryMap
    );

    switch (currIncorrect) {
      case 0:
        await cancellablePromise(
          speak(fromTimeout ? ttsStrings.timeout_1 : ttsStrings.scaffolding_1)
        );
        break;
      case 1:
        await cancellablePromise(
          speak(
            fromTimeout
              ? ttsStrings.timeout_2({
                  category,
                  word1,
                  letter,
                  word2,
                })
              : ttsStrings.scaffolding_2({
                  category,
                  word1,
                  letter,
                  word2,
                })
          )
        );
        break;
      default:
        logSessionInfo('GSF_puzzle_fail', { hardMode: hardModeRef.current });
        setPuzzleEnded(true);
        if (!fromTimeout) {
          await sleep(250);
        }
        await handleShowFinalAnswer();
        handlingIncorrect.current = false;
        return;
    }

    setIsIncorrect(false);
    setSelectionWord(undefined);
    setInteractive(true);
    handlingIncorrect.current = false;

    startGuessTimer();
  }

  const handleShowFinalAnswer = async () => {
    if (!props.isMountedRef.current) {
      return;
    }

    GSFResultService.addPuzzleResult(0);

    setIsIncorrect(false);
    setSelectionWord(undefined);

    const { category, word1, word2, letter } = getClues(
      testWordsRef.current,
      correctWordRef.current,
      categoryMap
    );

    const speakPromise = speak(
      ttsStrings.show_answer({
        correctWord: correctWordRef.current,
        category,
        word1,
        letter,
        word2,
      })
    );
    setTimeout(() => setShowAnswer(true), 1000);
    const animationPromise = sleep(2500);
    await cancellablePromise(Promise.all([speakPromise, animationPromise]));
    setIsCorrect(true);
    goToNextStage();
  }

  return (
    <Container className="gsf-modal">
      <GSFContent
        testWords={testWords}
        correctWord={correctWord}
        optionWords={optionWords}
        hiddenOptionWords={hiddenOptionWords}
        isCorrect={isCorrect}
        isIncorrect={isIncorrect}
        selectionWord={selectionWord}
        highlightedIndices={highlightedIndices}
        showAnswer={showAnswer}
        interactive={interactive}
        handleGuess={handleGuess}
        currentPuzzleEnded={puzzleEnded}
        isiPad={isiPad}
      />
    </Container>
  );
}
